import { useApolloClient } from '@apollo/client';
import { SnackbarKey } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  getConnectionHealth,
  getConnectionHistory,
} from '@/graphql/connectionHealth';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useDocumentDownload } from '@/modules/files/hooks/useFileDownload';
import { DocumentType, WorkflowTrackerStatus } from '@/types/schema';
import { getAppVersion } from '@/utils/environmentUtils';

import { TrackerDownloadSource } from '../contexts/WorkflowTrackerDownloads.context';
import { NotificationContainer_WorkflowTrackerFragment } from '../graphql/CurrentUserWorkflowTrackersContext.generated';
import {
  WorkflowTrackerDownloadDocument,
  WorkflowTrackerDownloadQuery,
  WorkflowTrackerDownloadQueryVariables,
} from '../graphql/WorkflowTrackerDownload.generated';

// Stop waiting for an automatic download after 15 minutes; Activities retains its status.
const DOWNLOAD_WAIT_TIMEOUT_MS = 15 * 60_000;
const DOWNLOAD_POLL_INTERVAL_MS = 5_000;
// Capture a stalled wait once, without fetching data or attempting recovery.
const DOWNLOAD_DIAGNOSTIC_DELAY_MS = 60_000;

type ExportStage =
  | 'accepted'
  | 'still_waiting'
  | 'completion_observed'
  | 'output_unavailable'
  | 'generation_failed'
  | 'download_returned'
  | 'download_failed'
  | 'wait_expired';

interface PendingDownload {
  source: TrackerDownloadSource;
  feedbackKey?: SnackbarKey;
  fetching: boolean;
  startedAt: number;
  reportedWait: boolean;
  phase: 'awaiting_completion' | 'downloading';
  expiresAt: number;
}

// Keep this hook in the user-level provider so closing an export modal does
// not cancel its download. Only attempts started in this session are watched.
export function useWorkflowTrackerDownloads(
  userId: string | undefined,
  trackers: NotificationContainer_WorkflowTrackerFragment[]
) {
  const client = useApolloClient();
  const pending = useRef(new Map<string, PendingDownload>());
  const registered = useRef(new Set<string>());
  // Diagnostics continue after the download leaves the generation wait queue.
  const observed = useRef(new Map<string, PendingDownload>());
  const [revision, setRevision] = useState(0);
  const session = useMemo(() => ({ userId }), [userId]);
  const activeSession = useRef<typeof session | undefined>(undefined);
  const [presentationDownloads, setPresentationDownloads] = useState<{
    session: typeof session;
    ids: string[];
  }>();
  const [downloadDocument] = useDocumentDownload();
  const { showFeedback, hideFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const report = useCallback(
    (trackerId: string, request: PendingDownload, stage: ExportStage) => {
      if (activeSession.current !== session) return;
      if (
        stage !== 'accepted' &&
        stage !== 'completion_observed' &&
        stage !== 'still_waiting'
      ) {
        if (observed.current.get(trackerId) === request)
          observed.current.delete(trackerId);
        if (request.feedbackKey !== undefined)
          hideFeedback(request.feedbackKey);
      }
      if (stage !== 'completion_observed' && stage !== 'still_waiting') {
        // Include attempts whose output is still downloading, not only generation.
        setPresentationDownloads({
          session,
          ids: Array.from(observed.current.values()).flatMap(({ source }) =>
            source.kind === 'presentation' ? [source.presentationId] : []
          ),
        });
      }
      try {
        trackUserEvent('document export diagnostic', {
          stage,
          phase: request.phase,
          workflow_tracker_id: trackerId,
          ...(request.source.kind === 'document-summary'
            ? { source_document_id: request.source.documentId }
            : { presentation_id: request.source.presentationId }),
          elapsed_ms: Date.now() - request.startedAt,
          frontend_release: getAppVersion(),
          ...getConnectionHealth(),
          // Successful milestones keep the small snapshot. Only stalled or
          // failed attempts upload the bounded transition history.
          ...(stage !== 'accepted' &&
          stage !== 'completion_observed' &&
          stage !== 'download_returned'
            ? { connection_history: getConnectionHistory() }
            : {}),
        });
      } catch {
        // Diagnostic failures must not interrupt an export.
      }
    },
    [session, trackUserEvent, hideFeedback]
  );

  useEffect(() => {
    const requests = pending.current;
    const observations = observed.current;
    const registrations = registered.current;
    activeSession.current = session;
    return () => {
      activeSession.current = undefined;
      for (const request of observations.values()) {
        if (request.feedbackKey !== undefined)
          hideFeedback(request.feedbackKey);
      }
      requests.clear();
      observations.clear();
      registrations.clear();
    };
  }, [session, hideFeedback]);

  const awaitTrackerDownload = useCallback(
    (trackerId: string, source: TrackerDownloadSource) => {
      // An enqueue response from a previous user session must not add work here.
      if (!session.userId || activeSession.current !== session) return;
      if (registered.current.has(trackerId)) return;
      registered.current.add(trackerId);
      const request: PendingDownload = {
        source,
        fetching: false,
        startedAt: Date.now(),
        reportedWait: false,
        phase: 'awaiting_completion',
        expiresAt: Date.now() + DOWNLOAD_WAIT_TIMEOUT_MS,
      };
      request.feedbackKey = showFeedback(
        'Generating export. Your download will start automatically.',
        { variant: 'info-low', persist: true }
      );
      pending.current.set(trackerId, request);
      observed.current.set(trackerId, request);
      report(trackerId, request, 'accepted');
      setRevision((value) => value + 1);
    },
    [session, report, showFeedback]
  );

  const observeTracker = useCallback(
    (
      tracker: Pick<
        NotificationContainer_WorkflowTrackerFragment,
        'id' | 'status' | 'documents'
      >
    ) => {
      if (activeSession.current !== session) return;
      const request = pending.current.get(tracker.id);
      if (!request) return;
      if (
        tracker.status !== WorkflowTrackerStatus.Completed &&
        tracker.status !== WorkflowTrackerStatus.Failed
      ) {
        return;
      }

      // Remove synchronously before downloading: repeated tracker updates must
      // not start the same download while the first one is still running.
      pending.current.delete(tracker.id);
      setRevision((value) => value + 1);
      if (tracker.status === WorkflowTrackerStatus.Failed) {
        report(tracker.id, request, 'generation_failed');
        showFeedback(
          request.source.kind === 'document-summary'
            ? 'The document summary export failed. Please try again.'
            : 'The presentation export failed. Please try again.'
        );
        return;
      }

      report(tracker.id, request, 'completion_observed');
      const expectedOutputType =
        request.source.kind === 'document-summary'
          ? DocumentType.GeneratedDocument
          : DocumentType.GeneratedPresentation;
      const generatedOutputs = (tracker.documents ?? []).filter((document) => {
        // Summary trackers retain the input document, which can itself be generated.
        if (
          request.source.kind === 'document-summary' &&
          document.id === request.source.documentId
        ) {
          return false;
        }

        return document.type === expectedOutputType;
      });

      const [output] = generatedOutputs;
      // Download only when this attempt has exactly one matching output.
      if (generatedOutputs.length !== 1 || !output) {
        report(tracker.id, request, 'output_unavailable');
        showFeedback(
          'The export completed, but its download is unavailable. Please try again.'
        );
        return;
      }

      // useDocumentDownload reports failures and shows download feedback.
      // A resolved call does not prove that the browser saved the file.
      request.phase = 'downloading';
      void downloadDocument(output.id).then(
        () => report(tracker.id, request, 'download_returned'),
        () => report(tracker.id, request, 'download_failed')
      );
    },
    [session, report, showFeedback, downloadDocument]
  );

  useEffect(() => {
    if (!session.userId || pending.current.size === 0) return;
    trackers.forEach(observeTracker);
    if (pending.current.size === 0) return;
    const nextExpiry = Math.min(
      ...Array.from(pending.current.values(), (request) => request.expiresAt)
    );
    const timeout = setTimeout(
      () => {
        for (const [id, request] of pending.current) {
          if (request.expiresAt <= Date.now()) {
            report(id, request, 'wait_expired');
            pending.current.delete(id);
          }
        }
        setRevision((value) => value + 1);
        showFeedback(
          'The export is taking longer than expected. Check Activities for its status.'
        );
      },
      Math.max(0, nextExpiry - Date.now())
    );
    return () => clearTimeout(timeout);
  }, [session, trackers, revision, observeTracker, showFeedback, report]);

  useEffect(() => {
    if (!session.userId || pending.current.size === 0) return;
    const poll = () => {
      for (const [id, request] of pending.current) {
        if (request.fetching || request.expiresAt <= Date.now()) continue;
        request.fetching = true;
        void client
          .query<
            WorkflowTrackerDownloadQuery,
            WorkflowTrackerDownloadQueryVariables
          >({
            query: WorkflowTrackerDownloadDocument,
            variables: { id },
            fetchPolicy: 'no-cache',
          })
          .then(({ data }) => {
            if (
              activeSession.current !== session ||
              pending.current.get(id) !== request
            )
              return;
            if (data.node?.__typename === 'WorkflowTracker')
              observeTracker(data.node);
          })
          .catch(() => {
            // A transient read failure must not cancel the export. Retry until
            // the bounded wait expires; stalled waits retain connection diagnostics.
          })
          .finally(() => {
            request.fetching = false;
          });
      }
    };
    // Reconcile immediately: completion can precede the enqueue response or
    // fall outside the Activities history page. Subscriptions remain the fast path.
    poll();
    const interval = setInterval(poll, DOWNLOAD_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [client, session, revision, observeTracker]);

  useEffect(() => {
    const unreported = Array.from(observed.current.values()).filter(
      (request) => !request.reportedWait
    );
    if (!unreported.length) return;
    const nextReportAt = Math.min(
      ...unreported.map(
        (request) => request.startedAt + DOWNLOAD_DIAGNOSTIC_DELAY_MS
      )
    );
    const timeout = setTimeout(
      () => {
        for (const [id, request] of observed.current) {
          if (
            !request.reportedWait &&
            Date.now() >= request.startedAt + DOWNLOAD_DIAGNOSTIC_DELAY_MS
          ) {
            request.reportedWait = true;
            report(id, request, 'still_waiting');
          }
        }
        setRevision((value) => value + 1);
      },
      Math.max(0, nextReportAt - Date.now())
    );
    return () => clearTimeout(timeout);
  }, [revision, session, report]);

  return {
    awaitTrackerDownload,
    pendingPresentationIds:
      presentationDownloads?.session === session
        ? presentationDownloads.ids
        : [],
  };
}
